/**
 * ConnectionMonitor - Multi-signal connection monitoring
 *
 * Key features:
 * - navigator.onLine events (fast but unreliable)
 * - Heartbeat to /admin/health (catches "online but API unreachable")
 * - Consecutive fetch failures (tracks actual save failures)
 * - 401/403 handling as "auth expired" (distinct from offline)
 *
 * Implementation: Task 3.0
 *
 * Usage:
 *   const monitor = new ConnectionMonitor({
 *     healthUrl: '/admin/health',
 *     failureThreshold: 3,
 *     heartbeatInterval: 5000,
 *     onStatusChange: (status) => updateConnectionBanner(status)
 *   })
 *
 *   // SaveQueue should call this after each fetch
 *   monitor.recordFetchResult(response.ok)
 *
 *   // Subscribe to status changes
 *   monitor.subscribe((isOnline) => console.log('Online:', isOnline))
 */

import { trackEditorEvent, EditorEvents } from './editor_analytics.js'
import { createLogger } from './logger.js'

// Phase 5: Shared logger with debug gating
const logger = createLogger('ConnectionMonitor')

// Connection states
export const ConnectionStatus = {
  ONLINE: 'online',
  OFFLINE: 'offline',
  RECONNECTING: 'reconnecting',
  AUTH_EXPIRED: 'auth_expired'
}

// Default configuration
const DEFAULT_OPTIONS = {
  healthUrl: '/admin/health',
  failureThreshold: 3, // Number of consecutive failures before declaring offline
  heartbeatInterval: 5000, // 5s heartbeat to meet "banner within 5s" SLA
  heartbeatTimeout: 5000, // Timeout for heartbeat request
  onStatusChange: null // Callback for status changes
}

export class ConnectionMonitor {
  /**
   * @param {Object} options - Configuration options
   * @param {string} options.healthUrl - Health check endpoint (default '/admin/health')
   * @param {number} options.failureThreshold - Consecutive failures before offline (default 3)
   * @param {number} options.heartbeatInterval - ms between heartbeats (default 5000)
   * @param {Function} options.onStatusChange - Callback: (status) => void
   */
  constructor(options = {}) {
    this.options = { ...DEFAULT_OPTIONS, ...options }

    // Connection state
    this.isOnline = navigator.onLine
    this.apiReachable = true
    this.consecutiveFailures = 0

    // Current status
    this.currentStatus = this.isOnline ? ConnectionStatus.ONLINE : ConnectionStatus.OFFLINE

    // Subscribers (Set of callbacks)
    this.listeners = new Set()

    // Timers
    this.heartbeatIntervalId = null

    // Event handlers (stored for cleanup)
    this.handleOnline = this.handleOnline.bind(this)
    this.handleOfflineEvent = this.handleOfflineEvent.bind(this)

    // Set up listeners
    this.setupListeners()

    // Start heartbeat
    this.startHeartbeat()

    logger.log('Initialized:', {
      healthUrl: this.options.healthUrl,
      failureThreshold: this.options.failureThreshold,
      heartbeatInterval: this.options.heartbeatInterval
    })
  }

  /**
   * Whether the connection is effectively usable
   * Combines navigator.onLine and API reachability
   */
  get effectivelyOnline() {
    return this.isOnline && this.apiReachable
  }

  /**
   * Set up browser online/offline event listeners
   * @private
   */
  setupListeners() {
    window.addEventListener('online', this.handleOnline)
    window.addEventListener('offline', this.handleOfflineEvent)
  }

  /**
   * Handle browser online event
   * @private
   */
  handleOnline() {
    logger.log('Browser online event')
    this.isOnline = true

    // Don't immediately declare online - verify API is reachable first
    this.setStatus(ConnectionStatus.RECONNECTING)
    this.checkApiReachability()
  }

  /**
   * Handle browser offline event
   * @private
   */
  handleOfflineEvent() {
    logger.log('Browser offline event')
    this.isOnline = false
    this.apiReachable = false
    this.handleOffline('browser_event')
  }

  /**
   * Record the result of a fetch operation
   * Called by SaveQueue after each save attempt
   *
   * @param {boolean} success - Whether the fetch succeeded
   * @param {number} statusCode - HTTP status code (optional)
   */
  recordFetchResult(success, statusCode = null) {
    if (success) {
      // Reset consecutive failures on success
      if (this.consecutiveFailures > 0) {
        logger.log('Fetch succeeded, resetting failure count')
        this.consecutiveFailures = 0

        // If we were offline/reconnecting, we're now online
        if (this.currentStatus !== ConnectionStatus.ONLINE) {
          this.apiReachable = true
          this.handleOnlineConfirmed('fetch_success')
        }
      }
    } else {
      // Check for auth expiration (401/403)
      if (statusCode === 401 || statusCode === 403) {
        this.handleAuthExpired()
        return
      }

      // Increment failure count
      this.consecutiveFailures++
      logger.log('Fetch failed, consecutive failures:', this.consecutiveFailures)

      // Check if threshold reached
      if (this.consecutiveFailures >= this.options.failureThreshold) {
        this.apiReachable = false
        this.handleOffline('fetch_failures')
      }
    }
  }

  /**
   * Start the heartbeat interval
   * @private
   */
  startHeartbeat() {
    // Clear any existing heartbeat
    this.stopHeartbeat()

    // Start new heartbeat
    this.heartbeatIntervalId = setInterval(() => {
      this.checkApiReachability()
    }, this.options.heartbeatInterval)

    // Do an initial check
    this.checkApiReachability()
  }

  /**
   * Stop the heartbeat interval
   * @private
   */
  stopHeartbeat() {
    if (this.heartbeatIntervalId) {
      clearInterval(this.heartbeatIntervalId)
      this.heartbeatIntervalId = null
    }
  }

  /**
   * Check API reachability via health endpoint
   * Uses AbortController + setTimeout for Safari compatibility
   * (AbortSignal.timeout is not supported in older Safari)
   * @private
   */
  async checkApiReachability() {
    // Don't check if browser is offline
    if (!navigator.onLine) {
      return
    }

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), this.options.heartbeatTimeout)

    try {
      const response = await fetch(this.options.healthUrl, {
        method: 'HEAD',
        cache: 'no-store',
        credentials: 'same-origin',
        signal: controller.signal
      })

      clearTimeout(timeoutId)

      // Check for auth expiration
      if (response.status === 401 || response.status === 403) {
        this.handleAuthExpired()
        return
      }

      if (response.ok) {
        // API is reachable
        if (!this.apiReachable || this.currentStatus !== ConnectionStatus.ONLINE) {
          this.apiReachable = true
          this.consecutiveFailures = 0
          this.handleOnlineConfirmed('heartbeat')
        }
      } else {
        // API returned error (but reachable)
        logger.warn(' Health check returned:', response.status)
        // Don't treat as offline - server is reachable but returning error
      }
    } catch (error) {
      clearTimeout(timeoutId)

      if (error.name === 'AbortError') {
        logger.warn(' Health check timed out')
      } else {
        logger.warn(' Health check failed:', error.message)
      }

      // If we're currently online, increment failure count
      if (this.currentStatus === ConnectionStatus.ONLINE) {
        this.consecutiveFailures++
        if (this.consecutiveFailures >= this.options.failureThreshold) {
          this.apiReachable = false
          this.handleOffline('heartbeat')
        }
      }
    }
  }

  /**
   * Handle confirmed online status
   * @private
   */
  handleOnlineConfirmed(source) {
    logger.log('Online confirmed via:', source)

    const wasOffline = this.currentStatus !== ConnectionStatus.ONLINE

    this.setStatus(ConnectionStatus.ONLINE)

    if (wasOffline) {
      // Track analytics
      trackEditorEvent(EditorEvents.CONNECTION_RESTORED, { source })

      // Notify all listeners
      this.notifyListeners(true)
    }
  }

  /**
   * Handle offline status
   * @private
   */
  handleOffline(source) {
    logger.log('Offline detected via:', source)

    const wasOnline = this.currentStatus === ConnectionStatus.ONLINE

    this.setStatus(ConnectionStatus.OFFLINE)

    if (wasOnline) {
      // Track analytics
      trackEditorEvent(EditorEvents.CONNECTION_LOST, { source })

      // Notify all listeners
      this.notifyListeners(false)
    }
  }

  /**
   * Handle auth expiration (401/403)
   * @private
   */
  handleAuthExpired() {
    logger.warn(' Auth expired detected')

    // Only notify once
    if (this.currentStatus === ConnectionStatus.AUTH_EXPIRED) {
      return
    }

    this.setStatus(ConnectionStatus.AUTH_EXPIRED)

    // Track analytics
    trackEditorEvent(EditorEvents.AUTH_EXPIRED, {})

    // Notify status change callback
    if (typeof this.options.onStatusChange === 'function') {
      this.options.onStatusChange(ConnectionStatus.AUTH_EXPIRED)
    }
  }

  /**
   * Set current status and notify callback
   * @private
   */
  setStatus(status) {
    if (this.currentStatus === status) {
      return
    }

    this.currentStatus = status
    logger.log('Status changed to:', status)

    // Notify status change callback
    if (typeof this.options.onStatusChange === 'function') {
      this.options.onStatusChange(status)
    }
  }

  /**
   * Subscribe to online/offline changes
   *
   * @param {Function} callback - (isOnline: boolean) => void
   * @returns {Function} Unsubscribe function
   */
  subscribe(callback) {
    this.listeners.add(callback)

    // Immediately invoke callback with current state so subscriber can react on load
    // This fixes the offline-at-load tight loop issue (P0-3)
    try {
      callback(this.effectivelyOnline)
    } catch (error) {
      console.error('[ConnectionMonitor] Initial callback error:', error)
    }

    return () => this.listeners.delete(callback)
  }

  /**
   * Notify all listeners of online/offline change
   * @private
   */
  notifyListeners(isOnline) {
    for (const callback of this.listeners) {
      try {
        callback(isOnline)
      } catch (error) {
        console.error('[ConnectionMonitor] Listener error:', error)
      }
    }
  }

  /**
   * Get current connection status
   * @returns {string} One of ConnectionStatus values
   */
  getStatus() {
    return this.currentStatus
  }

  /**
   * Force a connection check
   */
  forceCheck() {
    this.checkApiReachability()
  }

  /**
   * Destroy the monitor and clean up
   */
  destroy() {
    // Stop heartbeat
    this.stopHeartbeat()

    // Remove event listeners
    window.removeEventListener('online', this.handleOnline)
    window.removeEventListener('offline', this.handleOfflineEvent)

    // Clear listeners
    this.listeners.clear()

    logger.log('Destroyed')
  }
}
